import { Component, OnInit, Inject } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import {
  BentoAlertItemOptions,
  BentoComboboxOptions,
  BentoComboboxColumn,
  BentoToastService,
} from "@bento/bento-ng";
import { BehaviorSubject, forkJoin } from "rxjs";
import { Router } from "@angular/router";
import { LayoutService } from "src/app/core/services/layout.service";
import { EventsAdminService } from "../../services/events-admin.service";

import {
  Drawers,
  Category,
} from "../../../../core/models/tax-sort/tax-sort.modal";
import { TaxSortService } from "src/app/core/services/tax-sort/tax-sort.services";
import { PopmodalService } from "src/app/core/services/popupmodel.service";
import { distinctUntilChanged, map } from "rxjs/operators";

@Component({
  selector: "app-schema-browser",
  templateUrl: "./schema-browser.component.html",
  styleUrls: ["./schema-browser.component.scss"],
})
export class SchemaBrowserComponent implements OnInit {
  schemaForm: FormGroup;
  selectedSchema: any;
  selectedRegistration: any;

  dataStreamRegistration: BehaviorSubject<any> = new BehaviorSubject([]);
  products: any[];
  channels: any[] = [];
  schemas: any[] = [];
  isComboboxDisabled: boolean = false;
  isReadonly: boolean = false;

  comboboxOptions: BentoComboboxOptions = {
    searchable: false,
    columns: [
      {
        name: "name",
        width: "40px",
      },
    ] as BentoComboboxColumn[],
    labelFormatter: (row) => {
      return row.name;
    },
  };

  comboboxSchemaOptions: BentoComboboxOptions = {
    searchable: false,
    columns: [
      {
        name: "display_name",
        width: "40px",
      },
    ] as BentoComboboxColumn[],
    labelFormatter: (row) => {
      return row.display_name;
    },
  };

  comboboxOptionsProduct: BentoComboboxOptions = {
    searchable: true,
    autoSelect: false,
    labelFormatter: (item) => item.display_name, // Use labelFormatter to display the correct option
  };
  comboboxOptionsChannel: BentoComboboxOptions = {
    searchable: true,
    autoSelect: false,
  };
  comboboxOptionsSchema: BentoComboboxOptions = {
    searchable: true,
    autoSelect: false,
  };
  comboboxOptionsRegistration: BentoComboboxOptions = {
    searchable: true,
    autoSelect: false,
  };

  dataEmitter: BehaviorSubject<any> = new BehaviorSubject([]);
  dataProductsEmitter: BehaviorSubject<any> = new BehaviorSubject([]);
  dataChannelsEmitter: BehaviorSubject<any> = new BehaviorSubject([]);
  dataSchemasEmitter: BehaviorSubject<any> = new BehaviorSubject([]);
  dataEventsEmitter: BehaviorSubject<any> = new BehaviorSubject([]);

  eventRegistrations: any[];
  drawers: Drawers[];
  categories: Category[];
  selectedDrawerId: string;

  selectedDrawer: any;
  selectedProduct: any;
  selectedChannel: any;
  selectedEventRegistration: any;

  selectedProductId: string;
  selectedChannelId: string;
  selectedSchemalId: string;
  selectedEventRegistrationId: string;

  constructor(
    private fb: FormBuilder,
    private eventsAdminService: EventsAdminService,
    private layoutService: LayoutService,
    private router: Router,
    private toastService: BentoToastService,
    private popModalService: PopmodalService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadData();
  }

  getProducts() {
    this.eventsAdminService.fetchProducts().subscribe((res) => {
      this.products = res;
      this.dataProductsEmitter = new BehaviorSubject([]);
      this.dataProductsEmitter.next(this.products);
      this.selectedProductId = this.products[0]["id"];
      this.selectedProduct = this.products[0];
    });
  }

  initializeForm(): void {
    this.schemaForm = this.fb.group({
      eventProduct: [""],
      eventChannel: [""],
      eventSchema: ["", Validators.required],
      requestPayload: ["", [Validators.required, this.jsonValidator]],
      eventRegistration: ["", Validators.required],
      schemaSpecification: [""],
    });
  }

  loadData(): void {
    this.layoutService.setBusyLoader(true);
    this.eventsAdminService.getProductBasedOnEnvironment().subscribe((res) => {
      this.products = [res];
      this.dataProductsEmitter = new BehaviorSubject([]);
      this.dataProductsEmitter.next(this.products);

      for (const key of Object.keys(this.schemaForm.controls)) {
        // subscribing to valueChanges of each form control
        this.schemaForm.controls[key].valueChanges.subscribe((value) => {

          if (key === 'eventProduct'){
            this.selectedProduct = value;
            this.selectedProductId = value.id;
            this.eventsAdminService.fetchChannels().subscribe((res) => {
              this.channels = res;
              this.dataChannelsEmitter = new BehaviorSubject([]);
              this.dataChannelsEmitter.next(this.channels);
            });
          }

          if (key === 'eventChannel') {
            this.selectedChannel = value;
            this.selectedChannelId = value.id;
            this.fetchSchemasBasedOnChannel(Number(this.selectedChannelId));
          }

        });
      }
    });
    this.layoutService.setBusyLoader(false);
  }

  fetchEventRegistrationData(){
    this.eventsAdminService.getEventDetailsGFR().subscribe((res) => {
        let filteredRegistrations = res.filter(el => el.schema_id === this.selectedSchemalId);
        if (filteredRegistrations.length > 0 && this.eventRegistrations.length > 0){
          this.eventRegistrations = this.eventRegistrations.filter(itemA => filteredRegistrations.some(itemB => (itemB.id === itemA.id))); 
          if (this.eventRegistrations.length === 0){
            this.dataEventsEmitter = new BehaviorSubject([]);

            this.dataEventsEmitter.next([
              {
                "id": "No-Event-Registrations",
                "name": "No registered events found for this schema",
              }]);

          } else {
            this.dataEventsEmitter = new BehaviorSubject([]);
            this.dataEventsEmitter.next(this.eventRegistrations);
            this.schemaForm.patchValue({
              eventRegistration: "",
              
            });
            this.selectedEventRegistrationId = this.eventRegistrations[0]["id"];
            this.selectedEventRegistration = this.eventRegistrations[0];
          }
        }
    });
  }

  fetchSchemasBasedOnChannel(channelId: number): void {
    if (channelId !== null){
      let sendButton = document.getElementById("send-button");
      this.eventsAdminService.fetchSchemas(channelId).subscribe((res) => {
        this.schemas = res;

        if (this.schemas.length === 0) {
          this.dataSchemasEmitter = new BehaviorSubject([]);
          this.dataSchemasEmitter.next([
            {
              "id": "No-Schema",
              "display_name": "No Schemas found for this channel"
            }
          ]);
          this.schemaForm.patchValue({
            eventSchema: '',
            requestPayload: '',
            eventRegistration: '',
            schemaSpecification: ''
          });
          sendButton?.setAttribute("disabled", "true");
        } else {
          this.schemaForm.patchValue({
            eventSchema: '',
            requestPayload: '',
            eventRegistration: '',
            schemaSpecification: ''
          });
          this.dataSchemasEmitter = new BehaviorSubject([]);
          this.dataSchemasEmitter.next(this.schemas);
          sendButton?.removeAttribute("disabled");

        }
        
        this.schemaForm.valueChanges.pipe(
          map(schemaSelection => schemaSelection.eventSchema),
          distinctUntilChanged()
        ).subscribe((schemaSelection) => {
          this.selectedSchema = schemaSelection;
          this.selectedSchemalId = schemaSelection.id;
          if (schemaSelection.id !== null) {
            this.fetchEventsBasedOnProduct(Number(this.selectedProductId));
          }
        });
      });
    }
  }

  fetchEventsBasedOnProduct(productId: number): void {
    if (!Number.isNaN(productId)){
      this.eventsAdminService.fetchEvents().subscribe((res) => {
        this.eventRegistrations = res;
        this.fetchEventRegistrationData();
      });
    }
  }

  changeSchema(e): void {
    const selectedSchema = this.schemas.find((schema) => schema.id === e.id);
    if (selectedSchema) {
      this.schemaForm.patchValue({
        requestPayload: selectedSchema.example
          ? JSON.stringify(JSON.parse(selectedSchema.example), null, 2)
          : "",
        schemaSpecification: selectedSchema.specification
          ? JSON.stringify(JSON.parse(selectedSchema.specification), null, 2)
          : "",
      });
    }
  }

  onSubmit(): void {
    if (this.schemaForm.valid) {
      const formValue = this.schemaForm.value;
      
    if (formValue.eventRegistration.id == 'No-Event-Registrations'){
      this.toastService.open("Cannot send request for this event registration, Please select valid registration", {
        type: "warning",
        duration: 2500,
      });
      return;
    }
    
      this.eventsAdminService
        .sendSchemaPayload(
          formValue.eventRegistration.id,
          formValue.eventSchema.id,
          formValue.requestPayload
        )
        .subscribe(
          (response) => {
            this.toastService.open("Schema payload sent successfully!", {
              type: "success",
              duration: 2500,
            });
          },
          (error) => {
            this.toastService.open("Failed to send schema payload.", {
              type: "danger",
              duration: 2500,
            });
          }
        );
    } else {
      this.displayValidationErrors();
    }
  }

  displayValidationErrors(): void {
    const controls = this.schemaForm.controls;
    let errorMessages: string[] = [];

    if (controls.eventSchema.errors?.required) {
      errorMessages.push("Schema");
    }
    if (
      controls.requestPayload.errors?.required ||
      controls.requestPayload.errors?.invalidJson
    ) {
      errorMessages.push("Request Payload (must be a valid JSON)");
    }
    if (controls.eventRegistration.errors?.required) {
      errorMessages.push("Event Registration");
    }

    if (errorMessages.length > 0) {
      this.popModalService.alert(
        "warning",
        `Please fill in all required fields: ${errorMessages.join(", ")}.`
      );
    }
  }

  private jsonValidator(control: AbstractControl): ValidationErrors | null {
    try {
      JSON.parse(control.value);
      return null;
    } catch (e) {
      return { invalidJson: "Request Payload must be a valid JSON." };
    }
  }
}